import { useMemo, type ReactNode } from 'react';
import { slotsLabel, type DayPresence, type Employee, type PresenceSlot, type Team } from '@lemontaigu/staff';
import { fmtShortDayLabel, groupEmployeesByTeam, isWeekend, teamLabel } from './logic.js';

/** Column header: nickname when set, else first name — full names fit now that columns are wider. */
const columnLabel = (e: Employee): string => e.nickname || e.firstName;

// « Présence du mois » matrix (2026-07-19): one row per day, one column per
// employee grouped by team; each cell codes the declared/assigned thirds
// (matin/après-midi/soir) or a full-red Absent. Pure presentation — the data
// (presenceByDay) is computed by the caller (MonthView) via computeDayPresence,
// so this renders without any Supabase dependency (and stays screenshot-able).

/** Team → the CSS class carrying its --team-fg colour (null = neutral « Autres »). */
const teamClass = (team: Team | null): string => `team-${team ?? 'none'}`;

/** Compact French hours ('7,5 h') — the matrix cell is tight, no room for the full fmtHours label. */
const fmtHoursShort = (hours: number): string => `${hours.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} h`;

export function PresenceMatrix({
  employees,
  days,
  presenceByDay,
  today,
  hoursByDay,
  title = 'Présence du mois',
  canEditCell,
  onCellClick,
}: {
  employees: Employee[];
  /** Day-keys of the month, ascending — or a single day for a day-scoped view (staff@ Présence, 2026-07-21). */
  days: string[];
  /** date → (employeeId → presence). Missing employee = blank cell. */
  presenceByDay: Map<string, Map<string, DayPresence>>;
  today: string;
  /**
   * Optional (2026-07-19, bos@ only — reception@/staff@ decide what to pass):
   * date → (employeeId → actual worked hours, from computeDayWorkedHours).
   * Omit entirely to render exactly as before (no hours row in any cell) —
   * reception@ never passes this. staff@ passes a map scoped to just the
   * signed-in employee, so every other column simply shows no hours.
   */
  hoursByDay?: Map<string, Map<string, number>>;
  /** Section heading — defaults to the month view's label; staff@ Présence passes "Présence du jour" for its single-day render. */
  title?: string;
  /**
   * Quick-edit affordance (2026-08-04). A cell becomes a button when
   * `canEditCell` says so, and clicking it calls `onCellClick`. Both omitted =
   * the read-only matrix this has always been (reception@ passes neither).
   *
   * The predicate is the CALLER's, deliberately: bos@ answers « any employee »
   * from a capability, staff@ answers « only my own column » from its own id.
   * This component holds no authorization opinion of its own — it renders the
   * affordance it is told to, and the database is still the gate that decides.
   */
  canEditCell?: (employeeId: string) => boolean;
  onCellClick?: (employeeId: string, date: string) => void;
}) {
  const teamGroups = useMemo(() => groupEmployeesByTeam(employees), [employees]);
  const columnEmployees = useMemo(() => teamGroups.flatMap((g) => g.members), [teamGroups]);
  if (columnEmployees.length === 0) return null;

  return (
    <section className="pmx-section">
      <h3 className="board-section-title">{title}</h3>
      <div className="pmx-legend">
        <span className="pmx-legend-seg">
          <span className="pmx-slots">
            <i className="pmx-seg on" />
            <i className="pmx-seg" />
            <i className="pmx-seg" />
          </span>
          matin · après-midi · soir (rempli = présent·e)
        </span>
        <span className="pmx-legend-abs">Absent·e</span>
        {hoursByDay && <span className="pmx-legend-hours">7,5 h = heures pointées ce jour</span>}
      </div>
      <div className="pmx-scroll">
        <table className="presence-matrix">
          <colgroup>
            <col className="pmx-col-day" />
            {columnEmployees.map((e) => (
              <col key={e.id} className="pmx-col-emp" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="pmx-corner" rowSpan={2}>
                Jour
              </th>
              {teamGroups.map((g) => (
                <th key={g.team ?? 'none'} className={`pmx-team ${teamClass(g.team)}`} colSpan={g.members.length}>
                  {g.team ? teamLabel(g.team) : 'Autres'}
                </th>
              ))}
            </tr>
            <tr>
              {columnEmployees.map((e) => (
                <th key={e.id} className={`pmx-name ${teamClass(e.team)}`} title={`${e.firstName} ${e.lastName}`}>
                  {columnLabel(e)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((key) => {
              const presence = presenceByDay.get(key);
              const hours = hoursByDay?.get(key);
              return (
                <tr key={key} className={isWeekend(key) ? 'is-weekend' : ''}>
                  <th className={`pmx-day${key === today ? ' is-today' : ''}`}>{fmtShortDayLabel(key)}</th>
                  {columnEmployees.map((e) => (
                    <PmxCell
                      key={e.id}
                      presence={presence?.get(e.id)}
                      team={e.team}
                      hours={hours?.get(e.id)}
                      onClick={
                        onCellClick && canEditCell?.(e.id) ? () => onCellClick(e.id, key) : undefined
                      }
                      label={`${columnLabel(e)} — ${fmtShortDayLabel(key)}`}
                    />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** One presence cell: full-red « Absent·e », else the three filled/empty thirds, else blank — plus an optional hours line (bos@/staff@). */
function PmxCell({
  presence,
  team,
  hours,
  onClick,
  label,
}: {
  presence: DayPresence | undefined;
  team: Team | null;
  hours: number | undefined;
  /** Set = this cell is a quick-edit button (2026-08-04); undefined = read-only, as before. */
  onClick?: () => void;
  /** Accessible name for the button form — « Quentin — lun. 3 ». */
  label: string;
}) {
  const hoursLabel = hours != null && hours > 0 ? <span className="pmx-hours">{fmtHoursShort(hours)}</span> : null;

  let cellClass: string;
  let cellTitle: string | undefined;
  let body: ReactNode;

  if (!presence) {
    cellClass = `pmx-cell ${teamClass(team)}`;
    body = <>{hoursLabel}</>;
  } else if (presence.absent) {
    cellClass = 'pmx-cell pmx-cell-absent';
    cellTitle = 'Absent·e';
    body = (
      <>
        <span className="pmx-abs">✕</span>
        {hoursLabel}
      </>
    );
  } else {
    const slots: PresenceSlot[] = [];
    if (presence.matin) slots.push('matin');
    if (presence.apresMidi) slots.push('apresMidi');
    if (presence.soir) slots.push('soir');
    cellClass = `pmx-cell ${teamClass(team)}`;
    cellTitle = slotsLabel(slots);
    body = (
      <>
        <span className="pmx-slots">
          <i className={`pmx-seg${presence.matin ? ' on' : ''}`} />
          <i className={`pmx-seg${presence.apresMidi ? ' on' : ''}`} />
          <i className={`pmx-seg${presence.soir ? ' on' : ''}`} />
        </span>
        {hoursLabel}
      </>
    );
  }

  if (!onClick) {
    return (
      <td className={cellClass} title={cellTitle}>
        {body}
      </td>
    );
  }
  // A real <button>, not a click handler on the <td>: the cell is a control
  // now, and keyboard reach + an accessible name come with the element.
  return (
    <td className={`${cellClass} is-editable`} title={cellTitle}>
      <button type="button" className="pmx-cell-btn" onClick={onClick} aria-label={`Modifier ${label}`}>
        {body}
      </button>
    </td>
  );
}
